/**
 * Shared utilities for generating API code snippets in Python and R.
 */

/**
 * Formats a JavaScript object as an R list with proper indentation
 */
export function formatRList(obj: Record<string, unknown>, indent: number): string {
  const spaces = " ".repeat(indent);
  const entries: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;

    const formattedValue = formatRValue(value, indent + 2);
    entries.push(`${spaces}${key} = ${formattedValue}`);
  }

  return entries.join(",\n");
}

function formatRValue(value: unknown, indent: number): string {
  if (typeof value === "string") {
    // Escape backslashes first, then double quotes
    const escaped = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "TRUE" : "FALSE";
  }
  if (Array.isArray(value)) {
    const spaces = " ".repeat(indent);
    const items = value.map((item) => {
      if (typeof item === "object" && item !== null) {
        return `list(\n${formatRList(item as Record<string, unknown>, indent + 2)}\n${spaces})`;
      }
      return formatRValue(item, indent);
    });
    return `list(\n${spaces}${items.join(`,\n${spaces}`)}\n${" ".repeat(indent - 2)})`;
  }
  if (typeof value === "object" && value !== null) {
    const spaces = " ".repeat(indent);
    return `list(\n${formatRList(value as Record<string, unknown>, indent + 2)}\n${spaces})`;
  }
  return "NULL";
}

/**
 * Generates Python code for an API request
 */
export function generatePythonCode(query: Record<string, unknown>, modelSlug: string): string {
  const queryJson = JSON.stringify(query, null, 2);
  return `query = ${queryJson}

result = pysynthbio.predict_query(
    model_id="${modelSlug}",
    query=query
)`;
}

/**
 * Generates R code for an API request
 */
export function generateRCode(query: Record<string, unknown>, modelSlug: string): string {
  return `query <- list(
${formatRList(query, 2)}
)

result <- predict_query(model_id = "${modelSlug}", query = query)`;
}
